import { Button, Col, Pagination, Row, Dropdown, Menu } from "antd";
import { pluralize, singularize } from "inflection";
import startCase from "lodash/startCase";
import PropTypes from "prop-types";
import React from "react";
import {
  MoreOutlined,
  ReloadOutlined,
  ExportOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import "./styles.css";

const Toolbar = ({
  itemName,
  page,
  total,
  selectedItemsCount,
  exportUrl,
  onPaginate,
  onRefresh,
  onMapView,
  paginationPosition,
}) => {
  // Mobile menu items
  const menuItems = [];

  if (onRefresh) {
    menuItems.push({
      key: "refresh",
      icon: <ReloadOutlined />,
      label: "Refresh",
      onClick: onRefresh,
    });
  }

  if (onMapView) {
    menuItems.push({
      key: "map",
      icon: <EnvironmentOutlined />,
      label: "View on Map",
      onClick: onMapView,
    });
  }

  if (exportUrl) {
    menuItems.push({
      key: "export",
      icon: <ExportOutlined />,
      label: "Export",
      onClick: () => window.open(exportUrl, "_blank"),
    });
  }

  const mobileMenu = <Menu items={menuItems} />;

  // Responsive detection for mobile
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  return (
    <div className="Toolbar">
      <Row
        className="Toolbar-content"
        style={isMobile ? { width: "100%", margin: 0, textAlign: "right" } : {}}
      >
        {/* Desktop actions */}
        <Col
          xxl={12}
          xl={12}
          lg={12}
          md={10}
          sm={0}
          xs={0}
          className="Toolbar-actions-desktop"
        >
          <div className="Toolbar-col">
            {onRefresh && (
              <div className="Toolbar-row">
                <Button
                  title="Refresh"
                  className="prst-button-mute"
                  size="large"
                  onClick={onRefresh}
                  data-cy="reflesh"
                  icon={<ReloadOutlined />}
                >
                  Refresh
                </Button>
              </div>
            )}

            {onMapView && (
              <div className="Toolbar-row">
                <Button
                  title="View on Map"
                  className="actionButton"
                  size="large"
                  onClick={onMapView}
                  icon={<EnvironmentOutlined />}
                >
                  View on Map
                </Button>
              </div>
            )}

            {exportUrl && (
              <div className="Toolbar-row">
                <a href={exportUrl} download>
                  <Button
                    title="Export Data"
                    className="actionButton"
                    size="large"
                    icon={<ExportOutlined />}
                  >
                    Export
                  </Button>
                </a>
              </div>
            )}
          </div>
        </Col>

        {/* Mobile actions dropdown */}
        <Col sm={4} xs={4} className="Toolbar-actions-mobile">
          {menuItems.length > 0 && (
            <Dropdown overlay={mobileMenu} placement="bottomLeft">
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          )}
        </Col>

        {/* Count and pagination */}
        <Col
          xxl={12}
          xl={12}
          lg={12}
          md={14}
          sm={20}
          xs={20}
          className="Toolbar-count-title"
        >
          <Row type="flex" justify="end" className="right-content">
            <Col
              xxl={10}
              xl={12}
              lg={13}
              md={12}
              sm={12}
              xs={12}
              className="Toolbar-count"
            >
              <div className="Toolbar-count-wrapper">
                {selectedItemsCount > 0 && (
                  <span className="Toolbar-count-selected">
                    {`${selectedItemsCount} selected`}
                  </span>
                )}
                <span className="Toolbar-count-total">
                  {`${total} ${
                    total > 1
                      ? startCase(pluralize(itemName))
                      : startCase(singularize(itemName))
                  }`}
                </span>
              </div>
            </Col>

            {onPaginate && total > 0 && paginationPosition === "top" && (
              <Col
                xxl={6}
                xl={7}
                lg={9}
                md={9}
                sm={12}
                xs={12}
                className="Toolbar-pagination"
              >
                <Pagination
                  simple
                  current={page}
                  defaultCurrent={page}
                  total={total}
                  defaultPageSize={10}
                  className="pagination"
                  onChange={onPaginate}
                  responsive
                />
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

Toolbar.propTypes = {
  itemName: PropTypes.string.isRequired,
  page: PropTypes.number,
  total: PropTypes.number,
  exportUrl: PropTypes.string,
  selectedItemsCount: PropTypes.number.isRequired,
  onPaginate: PropTypes.func,
  onRefresh: PropTypes.func,
  onMapView: PropTypes.func,
  paginationPosition: PropTypes.string,
};

Toolbar.defaultProps = {
  exportUrl: undefined,
  paginationPosition: "top",
  onPaginate: undefined,
  onRefresh: undefined,
  onMapView: undefined,
};

export default Toolbar;
